import { imageToCanvas, loadImageAsync } from '@/utils/file-kit';
import { getPointsBetweenAngle } from '@/utils/math-kit';
import {
    transformGlowToStage,
    transformLetterSpacingToStage,
    transformLineSpacingToStage,
    transformShadowToStage,
    transformStrokeToStage,
} from '@/utils/transform-kit';
import { pxToInch } from '@/utils/unit-kit';
import { TextAlignType, TextWritingType, type ILayerProps, type ISlide, type ITextLayerProps } from '@/version-types';

const getPPTListMarker = (listStyle: string) => {
    if (!listStyle) return;

    switch (listStyle) {
        case 'decimal':
            return {
                type: 'number',
            };
        case 'lower-latin':
            return {
                type: 'number',
                style: 'alphaLcPeriod',
            };
        case 'upper-roman':
            return {
                type: 'number',
                style: 'romanUcPeriod',
            };
        case 'disc':
            return {
                code: '2022',
            };
        case 'circle':
            return {
                code: '25cb',
            };
        case 'square':
            return {
                code: '25a0',
            };
        default:
            return true;
    }
};

const getPPTColor = (color: string) => {
    if (!color) return '000000';
    return color.substring(1, 7);
};

const getPPTVert = (writingMode: TextWritingType) => {
    return writingMode === TextWritingType.HORIZONTAL_TB
        ? 'horz'
        : writingMode === TextWritingType.VERTICAL_LR
          ? 'wordArtVertRtl'
          : 'eaVert';
};

// px 转 pt
// 经询问伟哥，暂时没有计算，但是先保留
const pxToPt = (px: number) => px;

export class PPTSaver {
    private layer: ILayerProps;
    private layerData: ITextLayerProps;

    // --- 以下属性是需要经过计算得到 ---

    private width: number;
    private height: number;
    private left: number;
    private top: number;
    private lineSpacing: number;
    private verticalAlign: string;

    constructor(layer: ILayerProps) {
        this.layer = layer;

        const layerData = layer.data as ITextLayerProps;
        this.layerData = layerData;

        let lineSpacing = layerData.lineSpacing;
        let width = layer.width;
        let height = layer.height;
        let left = layer.left;
        let top = layer.top;

        const fontSize = layerData.fontSize;
        const textAlign = layerData.textAlign;

        if (layer.height < lineSpacing * fontSize + (lineSpacing * fontSize) / 2) {
            // 单行文字
            const oldWidth = width;
            width = width + fontSize * 2;

            if (textAlign === TextAlignType.CENTER) {
                left = left - (width - oldWidth) / 2;
            } else if (textAlign === TextAlignType.RIGHT) {
                left = left - (width - oldWidth);
            }
            console.log('lineSpacing', lineSpacing, top, layerData);
            top = top + (lineSpacing - 1.1) * 0.5 * fontSize; // 理应-1.2，但实际偏移会更多
            lineSpacing = 1.2;
            height = fontSize * 1.2;
        }

        this.width = width;
        this.height = height;
        this.left = left;
        this.top = top;
        this.lineSpacing = lineSpacing;
        this.verticalAlign = layerData.verticalAlign || 'middle';
    }

    private async saveByScreenshot(slide: ISlide) {
        const screenshotUrl = this.layerData.screenshotUrl!;

        const img = await loadImageAsync(screenshotUrl);
        const canvas = imageToCanvas(img);
        const options: any = {
            x: pxToInch(this.left),
            y: pxToInch(this.top),
            w: pxToInch(this.width),
            h: pxToInch(this.height),
        };

        options.data = canvas.toDataURL();
        slide.addImage(options);
    }

    private saveByLayerData(slide: ISlide) {
        const width = this.width;
        const height = this.height;
        const left = this.left;
        const top = this.top;
        const lineSpacing = this.lineSpacing;
        const { rotate } = this.layer;
        const {
            listStyle,
            fontFamily,
            fontSize,
            bold,
            italic,
            letterSpacing,
            writingMode,
            lineThrough,
            textAlign,
            underline,
            color,
            verticalAlign,
        } = this.layerData;
        let verticalAlignInPPT: string = verticalAlign || 'middle';
        if (writingMode !== TextWritingType.HORIZONTAL_TB) {
            /** 不同书写方向，align,valign会自动映射正确，但vertialAlign的值与valgin的映射值相反 */
            switch (verticalAlign) {
                case 'top':
                    verticalAlignInPPT = 'bottom';
                    break;
                case 'bottom':
                    verticalAlignInPPT = 'top';
                    break;
                case 'center':
                    verticalAlignInPPT = 'middle';
                    break;
            }
        }

        const textboxOpts: Record<string, any> = {
            x: pxToInch(left),
            y: pxToInch(top),
            w: pxToInch(width),
            h: pxToInch(height),

            color: getPPTColor(color),

            fontSize: Math.floor(pxToPt(fontSize)),
            fontFace: fontFamily,
            lineSpacing: pxToPt(lineSpacing * fontSize),
            charSpacing: pxToPt(letterSpacing),
            bullet: getPPTListMarker(listStyle),
            bold,
            italic,
            rotate: rotate > 0 ? rotate : 360 + rotate, // 0 - 360
            underline: underline as any,
            align: textAlign,
            vert: getPPTVert(writingMode),
            wrap: true,
            _bodyProp: { wrap: 'none' },
            valign: verticalAlignInPPT,
        };
        console.log('verticalAlign', this.layerData.text, textAlign, verticalAlign);

        // 根据 lineThrough 设置 strike
        if (lineThrough) Reflect.set(textboxOpts, 'strike', true);

        // 根据 textGradient 设置 gradientColor
        const textGradient = this.layerData.textGradient;
        const gradientColor = (() => {
            if (textGradient && textGradient.colors.length > 0) {
                return {
                    angle: (textGradient.angle - 90) * 60000,
                    colors: textGradient.colors.map((item) => {
                        return {
                            value: item.value * 100000,
                            color: item.color,
                        };
                    }),
                };
            }
            return undefined;
        })();
        Reflect.set(textboxOpts, 'gradientColor', gradientColor);

        // 根据 textShadow 设置 shadow
        const textShadow = this.layerData.textShadow || [];
        if (textShadow.length > 0) {
            const shadowConfig = textShadow[0];
            const { x, y, color, blur, hidden } = shadowConfig;
            const shadow = {
                color: getPPTColor(color),
                blur: pxToPt(blur),
                offset: pxToPt(Math.sqrt(x * x + y * y)),
                angle: getPointsBetweenAngle({ x: 1, y: 0 }, { x, y }),
            };

            if (!hidden) Reflect.set(textboxOpts, 'shadow', shadow);
        }

        // 根据 textGlow 设置 glow
        const textGlow = this.layerData.textGlow || [];
        if (textGlow.length > 0) {
            const glowConfig = textGlow[0];
            const { width, color, hidden } = glowConfig;
            const glow = {
                color: getPPTColor(color),
                size: pxToPt(width),
                opacity: 0.5,
            };
            if (!hidden) Reflect.set(textboxOpts, 'glow', glow);
        }

        // 根据 textStroke 设置 outline
        const textStroke = this.layerData.textStroke || [];
        if (textStroke.length > 0) {
            const strokeConfig = textStroke[0];
            const { color, width, hidden } = strokeConfig;
            const outline = {
                color: getPPTColor(color),
                size: pxToPt(width / 2),
            };

            if (!hidden) Reflect.set(textboxOpts, 'outline', outline);
        }

        // pptxgenjs bug 暂时不接入分段编辑
        const textContents = this.layerData.textContents || [];
        const text = this.layerData.text || '';

        if (textContents.length > 0) {
            const texts = textContents.map((item) => {
                const options = {
                    color: getPPTColor(item.color),
                    fontSize: Math.floor(pxToPt(item.fontSize)),
                    ...(item.fontFamily && { fontFace: item.fontFamily }),
                    bold: item.bold,
                    italic: item.italic,
                    underline: item.underline as any,
                    strike: item.lineThrough,
                    breakLine: item.text.endsWith('\n'),
                };
                return { text: item.text, options };
            });
            slide.addText(texts, textboxOpts);
        } else {
            slide.addText(text, textboxOpts);
        }
    }

    /**
     * 保存文本图层信息至 ppt slide
     * @param slide - ppt slide
     * @param externalFonts - 外部传入的字体列表，可变对象，该方法会改变该数组成员
     */
    public save(slide: ISlide, externalFonts?: string[]) {
        const { shapeExtension, screenshotUrl, fontFamily } = this.layerData;

        if (shapeExtension && screenshotUrl) {
            this.saveByScreenshot(slide);
        } else {
            this.saveByLayerData(slide);
        }

        if (externalFonts) {
            externalFonts.push(fontFamily);
        }
    }
}
